import React, { createContext, useContext } from 'react';
import { cn } from '../../lib/utils';

type ToggleGroupType = 'single' | 'multiple';
type Size = 'sm' | 'md' | 'lg';
type Variant = 'default' | 'outline';

interface ToggleGroupContextValue {
  type: ToggleGroupType;
  value: string | string[] | undefined;
  onValueChange?: (value: string | string[]) => void;
  size: Size;
  variant: Variant;
}

const ToggleGroupContext = createContext<ToggleGroupContextValue | null>(null);

interface ToggleGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: ToggleGroupType;
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  size?: Size;
  variant?: Variant;
}

export const ToggleGroup: React.FC<ToggleGroupProps> = ({
  type = 'single',
  value,
  onValueChange,
  size = 'md',
  variant = 'default',
  className,
  children,
  ...rest
}) => {
  return (
    <ToggleGroupContext.Provider value={{ type, value, onValueChange, size, variant }}>
      <div
        role="group"
        className={cn(
          // No group border; provide spacing only
          'inline-flex items-center bg-background rounded-[2px] gap-0.5',
          className
        )}
        {...rest}
      >
        {children}
      </div>
    </ToggleGroupContext.Provider>
  );
};

interface ItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  'aria-label'?: string;
}

export const ToggleGroupItem: React.FC<ItemProps> = ({ value, className, children, ...rest }) => {
  const ctx = useContext(ToggleGroupContext);
  if (!ctx) throw new Error('ToggleGroupItem must be used within ToggleGroup');
  const selected = ctx.type === 'single'
    ? ctx.value === value
    : Array.isArray(ctx.value) && ctx.value.includes(value);

  // More compact paddings for a lighter segmented look
  const sizes: Record<Size, string> = { sm: 'h-7 px-2 text-xs', md: 'h-8 px-2 text-sm', lg: 'h-9 px-2 text-sm' };

  return (
    <button
      type="button"
      className={cn(
        // Add per-button light border and small radius
        'border border-input normal-case cursor-pointer rounded-md',
        'data-[state=on]:bg-accent data-[state=on]:text-accent-foreground',
        'hover:bg-muted/60',
        sizes[ctx.size],
        selected ? 'bg-accent text-accent-foreground' : 'bg-background',
        className
      )}
      data-state={selected ? 'on' : 'off'}
      onClick={() => {
        if (!ctx.onValueChange) return;
        if (ctx.type === 'single') ctx.onValueChange(value);
        else {
          const current = Array.isArray(ctx.value) ? ctx.value : [];
          const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
          ctx.onValueChange(next);
        }
      }}
      {...rest}
    >
      {children}
    </button>
  );
};
